import { computeHMAC, hashKey } from './crypto'
import { isLocalRelayHost, type DesktopMessage, type MobileMessage } from '@shared/relay-protocol'

type MobileRequest = Omit<MobileMessage, 'requestId'>

type MessageHandler = (msg: DesktopMessage) => void
type EventCallback = (data: any) => void

interface PendingRequest {
  resolve: (data: any) => void
  reject: (err: Error) => void
  timer: ReturnType<typeof setTimeout>
}

export class RelayClient {
  private ws: WebSocket | null = null
  private channelId: string = ''
  private channelKey: string = ''
  private host: string = ''
  private listeners = new Map<string, Set<EventCallback>>()
  private pendingRequests = new Map<string, PendingRequest>()
  /** Requests made while the socket was down, waiting on the next auth-ok. */
  private queue: Array<{ requestId: string; payload: string }> = []
  private reconnectAttempts = 0
  private maxReconnectDelay = 30000
  private baseReconnectDelay = 1000
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null
  private shouldReconnect = false
  private messageHandler: MessageHandler | null = null
  private _connected = false
  private onConnectionChange: ((connected: boolean) => void) | null = null

  get connected() {
    return this._connected
  }

  setConnectionChangeHandler(handler: (connected: boolean) => void) {
    this.onConnectionChange = handler
  }

  private setConnected(value: boolean) {
    if (this._connected !== value) {
      this._connected = value
      this.onConnectionChange?.(value)
    }
  }

  setMessageHandler(handler: MessageHandler) {
    this.messageHandler = handler
  }

  async connect(channelId: string, channelKey: string, host: string) {
    this.channelId = channelId
    this.channelKey = channelKey
    this.host = host
    this.shouldReconnect = true
    this.reconnectAttempts = 0
    await this.doConnect()
  }

  private async doConnect() {
    if (this.ws) {
      this.ws.onclose = null
      this.ws.onerror = null
      this.ws.onmessage = null
      this.ws.close()
      this.ws = null
    }

    const protocol = isLocalRelayHost(this.host) ? 'ws' : 'wss'
    const url = `${protocol}://${this.host}/api/channels/${this.channelId}/ws?role=mobile`

    try {
      this.ws = new WebSocket(url)
    } catch {
      this.scheduleReconnect()
      return
    }

    this.ws.onopen = async () => {
      this.reconnectAttempts = 0
      try {
        const timestamp = Date.now()
        // Worker verifies HMAC(channelId:timestamp, SHA256(channelKey))
        const keyHash = await hashKey(this.channelKey)
        const hmac = await computeHMAC(
          `${this.channelId}:${timestamp}`,
          keyHash
        )
        this.ws?.send(
          JSON.stringify({
            type: 'auth',
            channelId: this.channelId,
            timestamp,
            hmac,
          })
        )
        // Not connected yet — the socket is open but the worker drops any
        // message sent before `auth-ok`, so anything queued now would be
        // thrown away. `connected` flips when auth lands.
      } catch {
        this.setConnected(false)
      }
    }

    this.ws.onmessage = async (event) => {
      try {
        const msg = JSON.parse(event.data as string)
        await this.handleMessage(msg)
      } catch (err) {
        console.error('[relay] message parse error:', err)
      }
    }

    this.ws.onclose = (event) => {
      this.setConnected(false)
      // Requests already on the wire can't be retried — the desktop may have
      // run them (a commit, a spawned session) and only the reply was lost.
      // Requests still queued never left, so they simply wait for the next
      // connection.
      this.rejectInFlight('Connection closed')
      // Don't reconnect on auth failure (4003, 4004) or channel not found (4001)
      if (this.shouldReconnect && event.code < 4000) {
        this.scheduleReconnect()
      }
    }

    this.ws.onerror = () => {
      // onclose will fire after onerror
    }
  }

  private async handleMessage(msg: DesktopMessage | any) {
    // Auth accepted — the channel is live and queued work can go out.
    if (msg.type === 'auth-ok') {
      this.setConnected(true)
      this.flushQueue()
      return
    }

    // Request/response
    if (msg.type === 'response' && 'requestId' in msg) {
      const pending = this.pendingRequests.get(msg.requestId)
      if (pending) {
        this.pendingRequests.delete(msg.requestId)
        clearTimeout(pending.timer)
        pending.resolve(msg.data)
      }
      return
    }

    if (msg.type === 'error' && 'requestId' in msg) {
      const pending = this.pendingRequests.get(msg.requestId)
      if (pending) {
        this.pendingRequests.delete(msg.requestId)
        clearTimeout(pending.timer)
        pending.reject(new Error(msg.message))
      }
      return
    }

    // Mirrored main→renderer IPC event. The desktop forwards the argument
    // list verbatim; api-shim delivers it positionally so subscriptions match
    // Electron's behaviour exactly.
    if (msg.type === 'ipc-event') {
      this.emit(`ipc:${msg.channel}`, msg.args ?? [])
      return
    }

    // Realtime pushes
    if (msg.type === 'state-snapshot') { this.emit('state-snapshot', msg); return }

    // Forward all other messages to handler and event listeners
    this.messageHandler?.(msg)
    this.emit(msg.type, msg)
  }

  async request<T = any>(
    message: MobileRequest,
    timeoutMs = 30000
  ): Promise<T> {
    const requestId = crypto.randomUUID()
    const payload = JSON.stringify({ ...message, requestId })

    return new Promise<T>((resolve, reject) => {
      const timer = setTimeout(() => {
        this.pendingRequests.delete(requestId)
        this.queue = this.queue.filter((q) => q.requestId !== requestId)
        reject(new Error(`Request timed out: ${message.type}`))
      }, timeoutMs)

      this.pendingRequests.set(requestId, { resolve, reject, timer })

      // A phone drops its connection constantly — screen lock, tab switch,
      // walking between cells. Rejecting immediately turns every one of those
      // into a visible error in the UI, so calls made mid-reconnect wait for
      // the socket instead and go out once auth completes. They still fail on
      // their own timeout if the desktop never comes back.
      if (this._connected && this.ws?.readyState === WebSocket.OPEN) {
        this.ws.send(payload)
      } else {
        this.queue.push({ requestId, payload })
      }
    })
  }

  /**
   * Fire-and-forget frame. No requestId, no pending entry, no timeout.
   *
   * Deliberately NOT queued while the socket is down, unlike `request`. A
   * queued request is still the answer you wanted a moment later; a queued
   * cursor position is a lie about where someone's pointer is now, and a
   * queued typing claim would grab a lock the user has long since stopped
   * wanting. Presence is only ever true in the present tense.
   */
  send(message: unknown) {
    if (!this._connected || this.ws?.readyState !== WebSocket.OPEN) return
    try {
      this.ws.send(JSON.stringify(message))
    } catch {
      // A dropped presence frame is not worth surfacing.
    }
  }

  /** Send everything that piled up while the socket was down. */
  private flushQueue() {
    if (!this.ws || this.ws.readyState !== WebSocket.OPEN) return
    const queued = this.queue
    this.queue = []
    for (const item of queued) {
      // Dropped by a timeout while it sat in the queue.
      if (!this.pendingRequests.has(item.requestId)) continue
      this.ws.send(item.payload)
    }
  }


  on(event: string, callback: EventCallback) {
    if (!this.listeners.has(event)) {
      this.listeners.set(event, new Set())
    }
    this.listeners.get(event)!.add(callback)
  }

  off(event: string, callback: EventCallback) {
    this.listeners.get(event)?.delete(callback)
  }

  private emit(event: string, data: any) {
    this.listeners.get(event)?.forEach((cb) => {
      try {
        cb(data)
      } catch {
        // Ignore callback errors
      }
    })
  }

  private scheduleReconnect() {
    if (this.reconnectTimer) return
    const delay = Math.min(
      this.baseReconnectDelay * Math.pow(2, this.reconnectAttempts),
      this.maxReconnectDelay
    )
    this.reconnectAttempts++
    this.emit('reconnecting', { attempt: this.reconnectAttempts, delay })
    this.reconnectTimer = setTimeout(() => {
      this.reconnectTimer = null
      this.doConnect()
    }, delay)
  }

  /** Reject requests that were sent and will never get an answer. */
  private rejectInFlight(reason: string) {
    const queued = new Set(this.queue.map((q) => q.requestId))
    for (const [id, pending] of this.pendingRequests) {
      if (queued.has(id)) continue
      clearTimeout(pending.timer)
      pending.reject(new Error(reason))
      this.pendingRequests.delete(id)
    }
  }

  private rejectAllPending(reason: string) {
    this.queue = []
    for (const pending of this.pendingRequests.values()) {
      clearTimeout(pending.timer)
      pending.reject(new Error(reason))
    }
    this.pendingRequests.clear()
  }

  disconnect() {
    this.shouldReconnect = false
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer)
      this.reconnectTimer = null
    }
    this.rejectAllPending('Disconnected')
    if (this.ws) {
      this.ws.onclose = null
      this.ws.close()
      this.ws = null
    }
    this.setConnected(false)
  }
}

// Singleton instance
export const relay = new RelayClient()
